import PerlinNoise3D from "./noise";
import { Flowfield } from "./physics";

class Artist {
    canvas: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    canvasWidth: number;
    canvasHeight: number;

    pixelSizeX = 12;
    pixelSizeY = 12;
    pixelBorderX = 1;

    perlinNoise: PerlinNoise3D | undefined;

    flowfield: Flowfield | undefined;
    flowfieldSizeX: number | undefined;
    flowfieldSizeY: number | undefined;

    cursorX: number;
    cursorY: number;

    constructor(canvas: HTMLCanvasElement) {
        this.canvas = canvas
        this.ctx = canvas.getContext('2d')!;
        this.canvas.style.cursor = "none";
        this.canvasWidth = this.canvas.width;
        this.canvasHeight = this.canvas.height;
        this.cursorX = 0;
        this.cursorY = 0;
    }

    updateCanvasSize(width: number, height: number) {
        this.canvasWidth = width;
        this.canvasHeight = height;
        this.canvas.width  = width;
        this.canvas.height = height;
    }

    updateCursorPosition(cursorX: number, cursorY: number) {
        this.cursorX = cursorX;
        this.cursorY = cursorY;
    }

    resizeImageToCanvasSize() {
        const displayWidth  = this.canvas.clientWidth;
        const displayHeight = this.canvas.clientHeight;
        const needResize =  this.canvas.width  !== displayWidth ||
                            this.canvas.height !== displayHeight;
        if (needResize) {
            this.canvas.width  = displayWidth;
            this.canvas.height = displayHeight;
            this.canvasWidth = displayWidth;
            this.canvasHeight = displayHeight;
            if (this.flowfield) this.flowfield.setFieldSize(this.canvasWidth, this.canvasHeight);
        }
        return needResize;
    }

    drawBackground() {
        this.ctx.fillStyle = "black";
        this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
    }

    clear() {
        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    }

    drawImage(image: HTMLCanvasElement) {
        this.ctx.drawImage(image, 0, 0, this.canvasWidth, this.canvasHeight)
    }

    drawPixels(time: number) {
        if (!this.perlinNoise) {
            this.perlinNoise = new PerlinNoise3D(3, 0.5, 0, 255, 0.1);
            this.perlinNoise.noiseSeed(1);
        }
        time = time / 200;
        const canvasBorderX = (this.canvasWidth % (this.pixelSizeX + this.pixelBorderX)) / 2;
        const canvasBorderY = (this.canvasHeight % this.pixelSizeY) / 2;
        const numPixelsX = (this.canvasWidth - canvasBorderX) / (this.pixelSizeX + this.pixelBorderX) - 1;
        const numPixelsY = (this.canvasHeight - canvasBorderY) / this.pixelSizeY - 1;
        for (let x = 0; x < numPixelsX; x++) {
            for (let y = 0; y < numPixelsY; y++) {
                this.ctx.fillStyle = `rgb( ${this.perlinNoise.get(x, y, time)} 0 0 )`;
                this.ctx.fillRect(
                    canvasBorderX + x * (this.pixelSizeX + this.pixelBorderX),
                    canvasBorderY + y * this.pixelSizeY,
                    this.pixelSizeX,
                    this.pixelSizeY/3
                );
                this.ctx.fillStyle = `rgb( 0 ${this.perlinNoise.get(x, y, time)} 0 )`;
                this.ctx.fillRect(
                    canvasBorderX + x * (this.pixelSizeX + this.pixelBorderX),
                    canvasBorderY + y * this.pixelSizeY + this.pixelSizeY/3,
                    this.pixelSizeX,
                    this.pixelSizeY/3
                );
                this.ctx.fillStyle = `rgb( 0 0 ${this.perlinNoise.get(x, y, time)} )`;
                this.ctx.fillRect(
                    canvasBorderX + x * (this.pixelSizeX + this.pixelBorderX),
                    canvasBorderY + y * this.pixelSizeY + 2 * this.pixelSizeY/3,
                    this.pixelSizeX,
                    this.pixelSizeY/3
                );
            }
        }
    }

    drawText(text: string) {
        this.ctx.textAlign = 'right';
        this.ctx.textBaseline = 'top';
        this.ctx.font = "bold 48px serif";
        this.ctx.fillStyle = "white";
        this.ctx.fillText(text, this.canvasWidth - 20, 20);
    }

    createFlowfield(flowfieldSizeX = 20, flowfieldSizeY = 20, charge = 0.01) {
        this.flowfieldSizeX = flowfieldSizeX;
        this.flowfieldSizeY = flowfieldSizeY;
        this.flowfield = new Flowfield( this.flowfieldSizeX,  this.flowfieldSizeY, this.canvasWidth, this.canvasHeight, charge);
    }

    drawFlowfield(vectorLength: number) {
        if (this.flowfield) {
            this.ctx.lineWidth = 1;
            this.ctx.strokeStyle = "white";
            if (!this.flowfield.chargesUpdated) this.flowfield.updateAllCharges();
            this.flowfield.vectors.forEach((value, indexY) => {
                value.forEach((value, indexX) => {
                    const startPoint = this.flowfield!.getChargeOrigin(indexX, indexY);
                    const endPoint = startPoint.copy().add(value.copy().setMagnitude(vectorLength));
                    this.ctx.beginPath(); // Start a new path
                    this.ctx.moveTo(startPoint.x, startPoint.y); // Move the pen to (30, 50)
                    this.ctx.lineTo(endPoint.x, endPoint.y); // Draw a line to (150, 100)
                    this.ctx.stroke(); // Render the path
                })
            })
        }
    }

    drawParticles(radius: number) {
        if (this.flowfield && this.flowfield.particles.length > 0) {
            this.ctx.fillStyle = "white";
            this.flowfield.particles.forEach((value) => {
                this.ctx.beginPath();
                this.ctx.arc(value.location.x, value.location.y, radius, 0, 2 * Math.PI);
                this.ctx.fill();
            })
        }
    }
}

export default Artist